import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, apiFetchJson } from '../../lib/apiFetch.js';
import type {
  FleetDriversResponse,
  FleetListResponse,
  FleetScheduleResponse,
  FleetVehicleCreateResponse,
  FleetVehicleDetailResponse,
} from './contractsMirror.js';
import type { FleetSearch } from './searchParams.js';

/** Shared API envelope for write endpoints that return `{ ok }` with no payload. */
interface FleetOkResponse {
  ok: boolean;
  error?: string;
}

/**
 * TanStack Query hook for the COARSE fleet endpoint (the strangler-fig + coarse-endpoint proof,
 * CARVE-07). One query key per `{entryType, maintenanceCategory, archived}` — toggling any filter
 * drives exactly one refetch of `GET /api/v1/fleet?entryType=…&maintenanceCategory=…&archived=…`.
 * The SERVER computes the vehicle list + entry rollups + the MTD summary (22-09); the SPA only
 * renders the returned `FleetListResponse` (no client re-aggregation, no new N+1 —
 * threat T-22-tenant / CARVE-07).
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side
 * from the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a filter (one cache entry per filter combination). */
export function fleetQueryKey(search: FleetSearch): readonly unknown[] {
  return [
    'fleet',
    search.entryType ?? null,
    search.maintenanceCategory ?? null,
    search.archived ?? false,
    search.search ?? null,
    search.dateFrom ?? null,
    search.dateTo ?? null,
  ] as const;
}

/** Build the coarse-endpoint request path with the active filters (no tenant param). */
export function fleetRequestPath(search: FleetSearch): string {
  const params = new URLSearchParams();
  if (search.entryType) params.set('entryType', search.entryType);
  if (search.maintenanceCategory) params.set('maintenanceCategory', search.maintenanceCategory);
  if (search.archived) params.set('archived', '1');
  if (search.search) params.set('search', search.search);
  if (search.dateFrom) params.set('dateFrom', search.dateFrom);
  if (search.dateTo) params.set('dateTo', search.dateTo);
  const qs = params.toString();
  return qs ? `/api/v1/fleet?${qs}` : '/api/v1/fleet';
}

export function useFleetQuery(search: FleetSearch) {
  return useQuery<FleetListResponse>({
    queryKey: fleetQueryKey(search),
    queryFn: () => apiFetchJson<FleetListResponse>(fleetRequestPath(search)),
  });
}

export interface CreateFleetVehicleInput {
  display_name: string;
  unit_number?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  assigned_employee_id?: number | null;
  assigned_driver_name?: string | null;
  active?: boolean;
  notes?: string | null;
}

/** Create a vehicle and refresh fleet caches so cards and summary rollups stay in sync. */
export function useCreateFleetVehicleMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetVehicleCreateResponse, Error, CreateFleetVehicleInput>({
    mutationFn: (input) =>
      apiFetchJson<FleetVehicleCreateResponse>('/api/v1/fleet/vehicles', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Fetch the full vehicle detail (overview + reminders + documents + history). */
export function useFleetVehicleDetailQuery(vehicleId: number) {
  return useQuery<FleetVehicleDetailResponse>({
    queryKey: ['fleet', 'vehicle', vehicleId],
    queryFn: () => apiFetchJson<FleetVehicleDetailResponse>(`/api/v1/fleet/vehicles/${vehicleId}`),
    enabled: Number.isFinite(vehicleId) && vehicleId > 0,
  });
}

/** Fetch the driver-assignment picker options (active employees). */
export function useFleetDriversQuery() {
  return useQuery<FleetDriversResponse>({
    queryKey: ['fleet', 'drivers'],
    queryFn: () => apiFetchJson<FleetDriversResponse>('/api/v1/fleet/drivers'),
  });
}

/** Edit a vehicle (F7). Sends NO tenant id (SEC-11); invalidates all fleet caches. */
export function useUpdateFleetVehicleMutation(vehicleId: number) {
  const queryClient = useQueryClient();

  return useMutation<FleetVehicleCreateResponse, Error, CreateFleetVehicleInput>({
    mutationFn: (input) =>
      apiFetchJson<FleetVehicleCreateResponse>(`/api/v1/fleet/vehicles/${vehicleId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Archive a vehicle (F11). */
export function useArchiveFleetVehicleMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (vehicleId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/vehicles/${vehicleId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Restore an archived vehicle (F11). */
export function useRestoreFleetVehicleMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (vehicleId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/vehicles/${vehicleId}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

export interface AssignDriverInput {
  assigned_employee_id?: number | null;
  assigned_driver_name?: string | null;
}

/** Update the driver assignment + record history (F9). */
export function useAssignDriverMutation(vehicleId: number) {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, AssignDriverInput>({
    mutationFn: (input) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/vehicles/${vehicleId}/assignment`, {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

// ─── Entries + receipts + documents (write surfaces over R2, 22-26) ───────────

export interface FleetEntryInput {
  vehicle_id: number;
  entry_type: 'fuel' | 'maintenance';
  entry_date: string;
  vendor?: string | null;
  amount: number;
  odometer?: number | null;
  gallons?: number | null;
  service_type?: string | null;
  maintenance_category?: string | null;
  notes?: string | null;
  /** Optional receipt file — sent as multipart so the browser adds the boundary. */
  receipt?: File | null;
  /** Edit-only: clear the stored receipt. */
  remove_receipt?: boolean;
}

/** Build multipart FormData for an entry write (only appends fields that are set). */
function buildEntryFormData(input: FleetEntryInput): FormData {
  const form = new FormData();
  form.set('vehicle_id', String(input.vehicle_id));
  form.set('entry_type', input.entry_type);
  form.set('entry_date', input.entry_date);
  form.set('amount', String(input.amount));
  if (input.vendor != null) form.set('vendor', input.vendor);
  if (input.odometer != null) form.set('odometer', String(input.odometer));
  if (input.gallons != null) form.set('gallons', String(input.gallons));
  if (input.service_type != null) form.set('service_type', input.service_type);
  if (input.maintenance_category != null)
    form.set('maintenance_category', input.maintenance_category);
  if (input.notes != null) form.set('notes', input.notes);
  if (input.remove_receipt) form.set('remove_receipt', '1');
  if (input.receipt) form.set('receipt', input.receipt);
  return form;
}

/**
 * Multipart POST/PATCH via `apiFetch` (NOT apiFetchJson-with-JSON) — we must pass a FormData body
 * with NO manual Content-Type so the browser sets the multipart boundary. `apiFetch` still attaches
 * the bearer token + CSRF; the tenant is server-derived (SEC-11).
 */
async function fleetMultipart<T>(path: string, method: string, form: FormData): Promise<T> {
  const res = await apiFetch(path, { method, body: form });
  const body = (await res.json().catch(() => null)) as
    | (T & { ok?: boolean; error?: string })
    | null;
  if (!res.ok || !body || body.ok === false) {
    throw new Error(body?.error ?? `request_failed_${res.status}`);
  }
  return body as T;
}

/** Log a fuel/maintenance entry with an optional receipt (F12). */
export function useCreateFleetEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, FleetEntryInput>({
    mutationFn: (input) =>
      fleetMultipart<FleetOkResponse>('/api/v1/fleet/entries', 'POST', buildEntryFormData(input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Edit an entry, optionally replacing/removing its receipt (F13). */
export function useUpdateFleetEntryMutation(entryId: number) {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, FleetEntryInput>({
    mutationFn: (input) =>
      fleetMultipart<FleetOkResponse>(
        `/api/v1/fleet/entries/${entryId}`,
        'PATCH',
        buildEntryFormData(input),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Archive an entry (F14). */
export function useArchiveFleetEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (entryId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/entries/${entryId}/archive`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Restore an archived entry (F14). */
export function useRestoreFleetEntryMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (entryId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/entries/${entryId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

export interface FleetDocumentInput {
  vehicle_id: number;
  document_type: 'registration' | 'insurance' | 'inspection' | 'service_contract' | 'other';
  title: string;
  file: File;
  expiration_date?: string | null;
  notes?: string | null;
}

/** Upload a typed vehicle document with an optional expiration (F16). */
export function useUploadFleetDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, FleetDocumentInput>({
    mutationFn: (input) => {
      const form = new FormData();
      form.set('vehicle_id', String(input.vehicle_id));
      form.set('document_type', input.document_type);
      form.set('title', input.title);
      form.set('document', input.file);
      if (input.expiration_date) form.set('expiration_date', input.expiration_date);
      if (input.notes != null) form.set('notes', input.notes);
      return fleetMultipart<FleetOkResponse>('/api/v1/fleet/documents', 'POST', form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Archive a vehicle document (F17). */
export function useArchiveFleetDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (documentId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/documents/${documentId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

/** Restore an archived vehicle document (F17). */
export function useRestoreFleetDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, number>({
    mutationFn: (documentId) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/documents/${documentId}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}

// ─── Tenant-wide schedule + reminder quick-complete (F21/F10, 22-26) ──────────

/** Fetch the tenant-wide 30-day reminder + doc-expiration schedule. */
export function useFleetScheduleQuery() {
  return useQuery<FleetScheduleResponse>({
    queryKey: ['fleet', 'schedule'],
    queryFn: () => apiFetchJson<FleetScheduleResponse>('/api/v1/fleet/schedule'),
  });
}

export interface CompleteReminderInput {
  vehicleId: number;
  category: 'oil_change' | 'tire_rotation' | 'inspection';
  entry_date?: string;
  odometer?: number | null;
  amount?: number | null;
  vendor?: string | null;
  notes?: string | null;
}

/** Quick-complete a due reminder by logging the completing maintenance entry (F10). */
export function useCompleteReminderMutation() {
  const queryClient = useQueryClient();

  return useMutation<FleetOkResponse, Error, CompleteReminderInput>({
    mutationFn: ({ vehicleId, ...rest }) =>
      apiFetchJson<FleetOkResponse>(`/api/v1/fleet/vehicles/${vehicleId}/reminders/complete`, {
        method: 'POST',
        body: JSON.stringify(rest),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet'] });
    },
  });
}
